import React, { useMemo, useState } from "react";
import dayjs from "dayjs";
import {
    Area,
    CartesianGrid,
    ComposedChart,
    Line,
    ReferenceLine,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";

import { isMobileViewport } from "../../utils/responsive";
import { getPaddedDomain } from "../../utils/chartDomain";
import { formatCompactNumberKR } from "../../utils/numberFormat";
import { getThemeColors } from "../../utils/themeColors";
import FilterPill from "../common/filter_pill";
import Skeleton from "../common/skeleton";
import SectionError from "../stocks/section_error";

const RANGE_OPTIONS = [
    { key: "30d", label: "30D", days: 30 },
    { key: "3m", label: "3M", days: 90 },
    { key: "6m", label: "6M", days: 180 },
    { key: "all", label: "전체", days: null },
];

const krw = (n) => `₩${Math.round(Number(n) || 0).toLocaleString()}`;

const filterByRange = (points, rangeKey) => {
    if (!points.length) return points;
    const option = RANGE_OPTIONS.find((o) => o.key === rangeKey);
    if (!option || option.days === null) return points;
    const cutoff = dayjs(points[points.length - 1].time).subtract(option.days, "day").valueOf();
    return points.filter((p) => p.time >= cutoff);
};

/**
 * DeltaHistoryChart — 델타 잔고 추이 (DAS-179, History 에서 이동).
 *
 * BTC 적립분은 **스냅샷이 있는 날만** 뺄 수 있다(utils/deltaHistory 참조). 그래서 한 차트 안에
 * 성격이 다른 두 구간이 생긴다 — 뺀 구간은 실선, 못 뺀 구간은 회색 점선으로 그리고 경계에
 * 세로선을 세운다. 값을 지어내거나 과거를 잘라내는 대신 **그렇다고 말하는** 쪽을 택했다(설계 옵션 B).
 *
 * 시간이 지나 스냅샷이 쌓이면 점선 구간은 왼쪽으로 밀려 자연히 사라진다.
 */
function DeltaHistoryChart({ series, loading, error, onRetry }) {
    const [rangeKey, setRangeKey] = useState("6m");
    const C = getThemeColors();
    const isMobile = isMobileViewport();

    const basePoints = useMemo(() => (series && Array.isArray(series.points) ? series.points : []), [series]);

    const rangeToggle = (
        <div className="flex items-center gap-1.5">
            {RANGE_OPTIONS.map((option) => (
                <FilterPill
                    key={option.key}
                    active={option.key === rangeKey}
                    onClick={() => setRangeKey(option.key)}
                    variant="solid"
                >
                    {option.label}
                </FilterPill>
            ))}
        </div>
    );

    const header = (
        <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-semibold text-content-secondary">델타 잔고 추이</span>
                <span className="rounded-full bg-brand-subtle px-2 py-0.5 text-xs font-medium text-content-secondary">
                    BTC 적립분 제외
                </span>
            </div>
            {basePoints.length > 0 && rangeToggle}
        </div>
    );

    if (loading) {
        return (
            <div className="rounded-2xl border border-surface-border bg-surface-card p-3 shadow-card sm:p-4">
                <Skeleton variant="line" width="35%" height={14} />
                <Skeleton variant="rect" height={isMobile ? 200 : 300} className="mt-3" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="rounded-2xl border border-surface-border bg-surface-card p-3 shadow-card sm:p-4">
                <SectionError message="델타 잔고 추이를 불러오지 못했습니다." onRetry={onRetry} />
            </div>
        );
    }

    if (basePoints.length === 0) {
        return (
            <div className="rounded-2xl border border-surface-border bg-surface-card p-3 shadow-card sm:p-4">
                {header}
                <div className="mt-6 py-8 text-center text-xs text-content-muted">
                    아직 기록된 잔고 히스토리가 없습니다.
                </div>
            </div>
        );
    }

    const displayData = filterByRange(basePoints, rangeKey);
    // 잘라낸 구간 안에도 점선(BTC 포함) 구간이 남아 있는지는 다시 판단해야 한다 —
    // 30D 로 좁히면 경계가 화면 밖으로 나가 점선이 사라질 수 있다.
    const hasIncluded = displayData.some((p) => !p.btcExcluded);
    const boundaryTime =
        series?.boundaryTime && displayData.some((p) => p.time >= series.boundaryTime) && hasIncluded
            ? series.boundaryTime
            : null;

    // 두 계열은 **서로 다른 값**이다 (DAS-180).
    //   totalRaw = 코인 총잔고(BTC 포함) — 전 구간
    //   delta    = BTC 를 뺀 값 — 스냅샷 구간만 (없는 날은 null 이라 recharts 가 선을 끊는다)
    // 한 계열의 색만 경계에서 바꾸려면 두 점을 이어야 하고, 그 선분이 BTC 금액만큼의 낙하가 된다.
    // 기준이 바뀐 것을 실제 손실처럼 보이게 하므로 그렇게 그리지 않는다 — 두 선의 간격이 곧 BTC 적립분이다.
    const chartData = displayData;

    // 두 계열이 함께 보여야 하므로 도메인도 둘을 모두 담는다
    const domain = getPaddedDomain(
        displayData.flatMap((p) => [p.totalRaw, p.delta]).filter((v) => Number.isFinite(v))
    );
    const height = isMobile ? 220 : 300;
    const yAxisWidth = isMobile ? 56 : 72;

    const footnote = hasIncluded
        ? `${series?.snapshotSince ? dayjs(series.snapshotSince).format("YYYY-MM-DD") : "스냅샷 시작일"}부터 BTC 적립분을 뺀 델타 잔고(실선)를 함께 그립니다. 그 이전은 잔고 기록에 코인별 내역이 없어 총잔고(점선)만 있습니다 — 두 선의 간격이 BTC 적립분입니다.`
        : "표시된 구간 전체에 BTC 스냅샷이 있어 델타 잔고를 그릴 수 있습니다.";

    return (
        <div className="rounded-2xl border border-surface-border bg-surface-card p-3 shadow-card sm:p-4">
            {header}

            <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-content-muted">
                <span className="inline-flex items-center gap-1">
                    <span className="inline-block h-0.5 w-3 rounded-full" style={{ backgroundColor: C.gain }} aria-hidden="true" />
                    델타 잔고 (BTC 제외)
                </span>
                <span className="inline-flex items-center gap-1">
                    <span
                        className="inline-block w-3"
                        style={{ borderTop: `2px dashed ${C.contentMuted}` }}
                        aria-hidden="true"
                    />
                    코인 총잔고 (BTC 포함)
                </span>
            </div>

            <div className="mt-3">
                <ResponsiveContainer width="100%" height={height}>
                    <ComposedChart data={chartData} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
                        <CartesianGrid stroke={C.surfaceElevated} strokeDasharray="3 3" vertical={false} />
                        <XAxis
                            dataKey="time"
                            type="number"
                            scale="time"
                            domain={["dataMin", "dataMax"]}
                            tickFormatter={(v) => dayjs(v).format("MM-DD")}
                            tick={{ fontSize: 12, fill: C.contentMuted }}
                            tickLine={false}
                            axisLine={false}
                        />
                        <YAxis
                            width={yAxisWidth}
                            domain={domain}
                            tickFormatter={(v) => `₩${formatCompactNumberKR(v)}`}
                            tick={{ fontSize: 12, fill: C.contentMuted }}
                            tickLine={false}
                            axisLine={false}
                        />
                        <Tooltip
                            contentStyle={{
                                backgroundColor: C.surfaceElevated,
                                border: `1px solid ${C.surfaceBorder}`,
                                borderRadius: 12,
                                fontSize: 12,
                            }}
                            labelFormatter={(v) => dayjs(v).format("YYYY-MM-DD HH:mm")}
                            formatter={(value, name) => [krw(value), name === "delta" ? "델타 잔고 (BTC 제외)" : "코인 총잔고 (BTC 포함)"]}
                        />
                        {boundaryTime && (
                            <ReferenceLine
                                x={boundaryTime}
                                stroke={C.warning}
                                strokeDasharray="4 3"
                                label={{ value: "BTC 제외 시작", position: "insideTopRight", fill: C.warning, fontSize: 11 }}
                            />
                        )}
                        <Area
                            type="monotone"
                            dataKey="delta"
                            stroke="none"
                            fill={C.gain}
                            fillOpacity={0.06}
                            isAnimationActive={false}
                            connectNulls={false}
                        />
                        <Line
                            type="monotone"
                            dataKey="totalRaw"
                            stroke={C.contentMuted}
                            strokeWidth={1.8}
                            strokeDasharray="5 4"
                            dot={false}
                            isAnimationActive={false}
                        />
                        <Line
                            type="monotone"
                            dataKey="delta"
                            stroke={C.gain}
                            strokeWidth={2.2}
                            dot={false}
                            isAnimationActive={false}
                            connectNulls={false}
                        />
                    </ComposedChart>
                </ResponsiveContainer>
            </div>

            <div className="mt-3 text-xs leading-relaxed text-content-muted">{footnote}</div>
        </div>
    );
}

export default DeltaHistoryChart;
